import { useReducedMotion } from "@reactuses/core";
import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useEffectEvent,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { tv } from "tailwind-variants";

import { ShimmerLoading } from "../shimmer/ShimmerLoading";
import { applyRevealStagger } from "./revealStagger";
import { getOutlineMaskStyle, getStagger, type SkeletonBlock } from "./skeletonLayout";
import {
    getInitialStage,
    getNextStage,
    getPhase,
    isDrawingStage,
    type SkeletonRevealPhase,
} from "./skeletonRevealStage";
import { useSkeletonBlocks } from "./useSkeletonBlocks";

export type { SkeletonRevealPhase } from "./skeletonRevealStage";

const DRAW_DURATION_MS = 950;
/**
 * The draw's ease-in-out curve barely moves in its last stretch, so the outline
 * already looks closed here; moving on then avoids a dead pause before the
 * shimmer or reveal.
 */
const DRAW_VISIBLY_DONE_RATIO = 0.9;
/**
 * Data that arrives sooner waits until here, then reveals while the outlines
 * keep drawing; any earlier and the outlines would flash in and straight out.
 */
const MIN_DRAW_BEFORE_REVEAL_MS = 570;
/**
 * A mid-draw reveal fades each outline out over its remaining draw time, so it
 * keeps drawing until it's gone; this floor keeps a late reveal from snapping.
 */
const MIN_MID_DRAW_FADE_MS = 300;
const STAGGER_STEP_MS = 40;
/** Kept short: later blocks are often below the fold, yet the whole draw waits for them. */
const STAGGER_MAX_SPREAD_MS = 240;
/**
 * Per content element; the whole reveal also spans the stagger. Must be at
 * least `OVERLAY_FADE_DURATION_MS`: settling unmounts the fading overlay.
 */
const REVEAL_DURATION_MS = 1600;
/** Opacity is fully resolved by the time the blur looks done (~40% of `REVEAL_DURATION_MS`). */
const REVEAL_FADE_DURATION_MS = REVEAL_DURATION_MS / 2;
/** The reveal's strong ease-out has resolved ~90% of an element by here; the rest is a long tail. */
const REVEAL_LOOKS_DONE_RATIO = 0.35;
const OVERLAY_FADE_DURATION_MS = 1000;
/** Fades in what isn't revealed per element, like section backgrounds. */
const SURFACE_FADE_DURATION_MS = 600;
const DEFAULT_FALLBACK_RADIUS = 4;
const DEFAULT_LOADING_LABEL = "Loading content";

type SkeletonRevealVariant = "default" | "light";

type SkeletonRevealProps = {
    /** While `false`, children render invisibly and only define the skeleton shape. */
    isLoaded: boolean;
    /** Real layout, rendered with placeholder data until `isLoaded`. */
    children: ReactNode;
    className?: string;
    /** `ShimmerLoading` variant used for every block. */
    variant?: SkeletonRevealVariant;
    /** Radius in px for blocks whose element has none, like text. */
    fallbackRadius?: number;
    /**
     * Announced to screen readers while the content is hidden. Prefer naming what
     * is loading, like "Loading your trips". Pass `""` when a surrounding region
     * already announces loading, e.g. for every card in a list.
     */
    loadingLabel?: string;
    /** Called on mount and whenever the phase changes, e.g. to restyle surroundings once `settled`. */
    onPhaseChange?: (phase: SkeletonRevealPhase) => void;
    /**
     * Called during a reveal once the content in the viewport looks resolved,
     * well before `settled`, e.g. to bring in UI that would compete with the blur.
     * Not called when no reveal plays; check for `settled` too.
     */
    onVisibleContentRevealed?: () => void;
};

/**
 * Structure-aware skeleton: measures the (placeholder) children and overlays
 * one `ShimmerLoading` block per leaf element and text line. Outlines draw
 * first; data that arrives mid-draw blurs in once the draw has run for a
 * moment, staggered like the draw, while the outlines fade out. The shimmer
 * only starts if data is still missing once the outlines finish. Content that
 * is ready on mount renders without any skeleton, and later loads skip the
 * draw and crossfade straight to the shimmer. Only content in the viewport
 * gets its own reveal animation; the rest fades in with the content.
 *
 * Tune measurement with `data-skeleton-ignore`, `data-skeleton-no-children`,
 * `data-skeleton-width`, and `data-skeleton-height`. Put
 * `data-skeleton-no-children` on composites layered with positioned overlays,
 * like an image card with a gradient and text, so they reveal as one element:
 * each revealing element gets its own stacking context, which would otherwise
 * paint its parts out of order until the reveal settles.
 */
export function SkeletonReveal({
    isLoaded,
    children,
    className,
    variant = "default",
    fallbackRadius = DEFAULT_FALLBACK_RADIUS,
    loadingLabel = DEFAULT_LOADING_LABEL,
    onPhaseChange,
    onVisibleContentRevealed,
}: SkeletonRevealProps) {
    const contentRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const visibleRevealDelayMsRef = useRef(0);
    const isReducedMotion = useReducedMotion();
    const [stage, setStage] = useState(() => getInitialStage(isLoaded));
    const [previousIsLoaded, setPreviousIsLoaded] = useState(isLoaded);

    if (previousIsLoaded !== isLoaded) {
        setPreviousIsLoaded(isLoaded);
        if (!isLoaded) {
            setStage(getNextStage(stage, "loadingStarted"));
        }
    }

    const [hasSettled, setHasSettled] = useState(stage === "settled");

    if (stage === "settled" && !hasSettled) {
        setHasSettled(true);
    }

    const phase = getPhase(stage, isLoaded);
    const isDrawing = isDrawingStage(stage);
    // The first load's outlines keep animating until the overlay unmounts:
    // dropping the animation would snap outlines that are still fading out.
    // Later loads skip the draw.
    const drawsOutlines = !hasSettled;
    const drawStartedAtRef = useRef<number | null>(null);
    const showsOverlay = phase !== "settled";
    // Content stays invisible until the draw minimum, even if data already arrived.
    const isContentHidden = phase === "drawing" || phase === "shimmering";
    // Blocks freeze once data arrives: the real content would reshape them
    // mid-draw, and the reveal's transforms would skew measurement.
    const blocks = useSkeletonBlocks({
        contentRef,
        overlayRef,
        isEnabled: !isLoaded,
        fallbackRadius,
    });
    const hasBlocks = blocks !== null;
    const stagger = getStagger(
        blocks?.length ?? 0,
        STAGGER_STEP_MS,
        STAGGER_MAX_SPREAD_MS,
    );
    const styles = skeletonReveal({ phase });

    const reportPhase = useEffectEvent((nextPhase: SkeletonRevealPhase) => {
        onPhaseChange?.(nextPhase);
    });

    useEffect(
        function notifyPhaseChange() {
            reportPhase(phase);
        },
        [phase],
    );

    const getDrawTimingsMs = useEffectEvent(() =>
        isReducedMotion
            ? { minimum: 0, finish: 0 }
            : {
                  minimum: MIN_DRAW_BEFORE_REVEAL_MS,
                  finish: DRAW_DURATION_MS * DRAW_VISIBLY_DONE_RATIO + stagger.spreadMs,
              },
    );

    // Deadlines are set once from the first measurement, so re-measures
    // (late fonts, images) and passing the minimum don't restart the draw.
    useEffect(
        function scheduleDraw() {
            if (!isDrawing || !hasBlocks) {
                return;
            }

            drawStartedAtRef.current = performance.now();
            const timings = getDrawTimingsMs();
            const minimumTimeout = setTimeout(() => {
                setStage((current) => getNextStage(current, "drawMinimumReached"));
            }, timings.minimum);
            const finishTimeout = setTimeout(() => {
                setStage((current) => getNextStage(current, "drawFinished"));
            }, timings.finish);

            return () => {
                clearTimeout(minimumTimeout);
                clearTimeout(finishTimeout);
            };
        },
        [isDrawing, hasBlocks],
    );

    const staggerContentReveal = useEffectEvent(() => {
        const content = contentRef.current;
        const overlay = overlayRef.current;

        if (!content || !overlay) {
            return undefined;
        }

        const revealStagger = applyRevealStagger(
            content,
            overlay.getBoundingClientRect(),
            blocks ?? [],
            stagger.stepMs,
        );
        visibleRevealDelayMsRef.current = revealStagger.visibleDelayMs;
        const drawStartedAt = drawStartedAtRef.current;

        // Mid-draw, each block fades from its own reveal delay, which equals its
        // draw delay, so every block has the same draw time left to fade over.
        if (isDrawing && drawStartedAt !== null && !isReducedMotion) {
            const remainingDrawMs =
                DRAW_DURATION_MS - (performance.now() - drawStartedAt);
            const fadeMs = Math.min(
                Math.max(remainingDrawMs, MIN_MID_DRAW_FADE_MS),
                OVERLAY_FADE_DURATION_MS,
            );

            overlay.style.setProperty(
                "--skeleton-overlay-fade-duration",
                `${fadeMs}ms`,
            );
            overlay.style.setProperty("--skeleton-overlay-fade-easing", "linear");
        }

        return () => {
            revealStagger.cleanup();
            overlay.style.removeProperty("--skeleton-overlay-fade-duration");
            overlay.style.removeProperty("--skeleton-overlay-fade-easing");
        };
    });

    // Layout effect: elements must carry their delay before the first revealing frame paints.
    useLayoutEffect(
        function staggerReveal() {
            if (phase !== "revealing") {
                return;
            }

            return staggerContentReveal();
        },
        [phase],
    );

    const getRevealTotalMs = useEffectEvent(
        () => REVEAL_DURATION_MS + stagger.spreadMs,
    );

    useEffect(
        function settleReveal() {
            if (phase !== "revealing") {
                return;
            }

            // Waits for the last staggered element, so settling never cuts its animation.
            const timeout = setTimeout(() => {
                setStage((current) => getNextStage(current, "revealFinished"));
            }, getRevealTotalMs());

            return () => {
                clearTimeout(timeout);
            };
        },
        [phase],
    );

    const notifyVisibleContentRevealed = useEffectEvent(() => {
        onVisibleContentRevealed?.();
    });
    const getVisibleRevealMs = useEffectEvent(() =>
        isReducedMotion
            ? 0
            : visibleRevealDelayMsRef.current +
              REVEAL_DURATION_MS * REVEAL_LOOKS_DONE_RATIO,
    );

    useEffect(
        function reportVisibleContentRevealed() {
            if (phase !== "revealing") {
                return;
            }

            const timeout = setTimeout(
                notifyVisibleContentRevealed,
                getVisibleRevealMs(),
            );

            return () => {
                clearTimeout(timeout);
            };
        },
        [phase],
    );

    const rootStyle = {
        "--skeleton-draw-duration": `${DRAW_DURATION_MS}ms`,
        "--skeleton-reveal-duration": `${REVEAL_DURATION_MS}ms`,
        "--skeleton-reveal-fade-duration": `${REVEAL_FADE_DURATION_MS}ms`,
        "--skeleton-overlay-fade-duration": `${OVERLAY_FADE_DURATION_MS}ms`,
        "--skeleton-overlay-fade-easing": "cubic-bezier(0.23, 1, 0.32, 1)",
        "--skeleton-surface-fade-duration": `${SURFACE_FADE_DURATION_MS}ms`,
    } as CSSProperties;

    return (
        <div
            className={styles.root({ className })}
            data-phase={phase}
            style={rootStyle}
        >
            {/* Stays mounted so screen readers notice its text changing. Outside the
                busy content, which some screen readers don't announce from. */}
            {loadingLabel && (
                <span role="status" className="sr-only">
                    {isContentHidden ? loadingLabel : null}
                </span>
            )}
            <div
                ref={contentRef}
                className={styles.content()}
                aria-busy={isContentHidden}
                inert={isContentHidden}
            >
                {children}
            </div>
            {showsOverlay && (
                <div ref={overlayRef} aria-hidden className={styles.overlay()}>
                    {blocks?.map((block, index) => (
                        <SkeletonRevealBlock
                            // biome-ignore lint/suspicious/noArrayIndexKey: blocks have no identity; index keys keep running draw animations alive across re-measures
                            key={index}
                            block={block}
                            delayMs={index * stagger.stepMs}
                            phase={phase}
                            drawsOutline={drawsOutlines}
                            variant={variant}
                            showsSwoosh={phase === "shimmering" && !isReducedMotion}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

const skeletonReveal = tv({
    slots: {
        root: "relative isolate grid",
        // Above the overlay, so the real content resolves on top of the fading skeleton.
        // No layer of its own: one content-sized layer would repaint on every frame
        // while elements inside it blur in, which stalls scrolling.
        content: "relative z-1 min-w-0",
        // Skips styling and painting the blocks while off-screen, e.g. for skeletons
        // further down a page; its size comes from `inset-0`, so the size containment
        // `content-visibility` applies there can't change it.
        overlay:
            "pointer-events-none absolute inset-0 z-0 overflow-clip content-visibility-auto",
    },
    variants: {
        phase: {
            drawing: {
                content: "opacity-0",
            },
            shimmering: {
                // Crossfades with the blocks fading in when a later load starts.
                content:
                    "opacity-0 transition-opacity duration-(--skeleton-overlay-fade-duration) ease-[ease]",
            },
            revealing: {
                content: [
                    "animate-skeleton-reveal-surface",
                    "[&_[data-skeleton-reveal-item]]:animate-skeleton-reveal-item motion-reduce:[&_[data-skeleton-reveal-item]]:animate-none",
                    // Elements containing media, like image cards, settle in like media themselves.
                    "[&_[data-skeleton-reveal-item]:has(img,video,picture,canvas)]:animate-skeleton-reveal-media-item motion-reduce:[&_[data-skeleton-reveal-item]:has(img,video,picture,canvas)]:animate-none",
                    // Own layers, so blurring doesn't repaint the page.
                    "[&_[data-skeleton-reveal-item]]:will-change-[opacity,filter] motion-reduce:[&_[data-skeleton-reveal-item]]:will-change-auto",
                    // Media rendered after the reveal started has no delay tag, but still settles in.
                    // Not inside a revealing element: it already settles in as a unit, and an animated
                    // descendant would paint above the element's own overlays, like image gradients.
                    // Not off-screen either, where the animation would only cost layers.
                    "[&_:is(img,video,picture,canvas):not([data-skeleton-reveal-item]_*,[data-skeleton-reveal-skip],[data-skeleton-reveal-skip]_*)]:animate-skeleton-reveal-media motion-reduce:[&_:is(img,video,picture,canvas)]:animate-none",
                    "[&_:is(img,video,picture,canvas)[data-skeleton-reveal-item]]:animate-skeleton-reveal-media-item motion-reduce:[&_:is(img,video,picture,canvas)[data-skeleton-reveal-item]]:animate-none",
                    // Transitions override animations, so one still running, like an
                    // image's load fade, would show the element ahead of its reveal.
                    // Important, to also beat unlayered component CSS.
                    "[&_:is([data-skeleton-reveal-item],img,video,picture,canvas)]:transition-none!",
                ],
            },
            settled: {},
        },
    },
});

const skeletonRevealBlock = tv({
    slots: {
        block: "absolute transition-opacity duration-(--skeleton-overlay-fade-duration) ease-(--skeleton-overlay-fade-easing) starting:opacity-0",
        fill: "absolute inset-0 rounded-[inherit] skeleton-fill-wipe",
        outline:
            "block size-full rounded-[inherit] gradient-border gradient-border-to-br",
    },
    variants: {
        phase: {
            drawing: {},
            shimmering: {},
            revealing: {
                // Fades out in the same reading order the outlines drew in.
                block: "opacity-0 delay-(--block-delay)",
            },
            settled: {},
        },
        // Separate from `phase` so a mid-draw reveal keeps drawing while it fades.
        drawsOutline: {
            true: {
                fill: "animate-skeleton-fill motion-reduce:animate-none",
                outline: "animate-skeleton-draw motion-reduce:animate-none",
            },
            false: {},
        },
        variant: {
            default: {
                outline:
                    "gradient-border-from-component-shimmer-loading-gradient-to gradient-border-via-component-shimmer-loading-gradient-to/30 gradient-border-to-component-shimmer-loading-gradient-to",
            },
            light: {
                outline:
                    "gradient-border-from-component-shimmer-loading-light-gradient-to gradient-border-via-component-shimmer-loading-light-gradient-to/30 gradient-border-to-component-shimmer-loading-light-gradient-to",
            },
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

type SkeletonRevealBlockProps = {
    block: SkeletonBlock;
    delayMs: number;
    phase: SkeletonRevealPhase;
    drawsOutline: boolean;
    variant: SkeletonRevealVariant;
    showsSwoosh: boolean;
};

function SkeletonRevealBlock({
    block,
    delayMs,
    phase,
    drawsOutline,
    variant,
    showsSwoosh,
}: SkeletonRevealBlockProps) {
    const styles = skeletonRevealBlock({ phase, drawsOutline, variant });
    const blockStyle = {
        left: block.x,
        top: block.y,
        width: block.width,
        height: block.height,
        borderRadius: block.borderRadius,
        "--block-delay": `${delayMs}ms`,
    } as CSSProperties;

    return (
        <div className={styles.block()} style={blockStyle}>
            <ShimmerLoading
                variant={variant}
                fill="none"
                showsSwoosh={showsSwoosh}
                className={styles.fill()}
            />
            <span className={styles.outline()} style={getOutlineMaskStyle(block)} />
        </div>
    );
}
